var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import axios from "axios";
class HttpClient {
    constructor(roadwayAPIBaseURL, loginURL, firebaseInstance) {
        this.firebase = firebaseInstance;
        this.loginURL = loginURL;
        let service = axios.create({
            baseURL: roadwayAPIBaseURL,
        });
        this.checkAuthToken = this.checkAuthToken.bind(this);
        this.rejectedErrorPromise = this.rejectedErrorPromise.bind(this);
        this.checkAuthTokenExpiry = this.checkAuthTokenExpiry.bind(this);
        this.updateConfigWithNewToken = this.updateConfigWithNewToken.bind(this);
        service.interceptors.request.use(this.checkAuthToken, this.rejectedErrorPromise);
        // apply interceptor on response to check if auth token has expired
        service.interceptors.response.use((response) => response, this.checkAuthTokenExpiry);
        this.service = service;
    }
    rejectedErrorPromise(error) {
        return Promise.reject(error);
    }
    replayRequest(requestConfig) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.service
                .request(requestConfig)
                .catch((error) => reject(error));
            resolve(response);
        }));
    }
    updateDefaultAuthorizationHeader(value) {
        this.service.defaults.headers.common.Authorization = value;
    }
    updateAuthorizationHeaderToken(token) {
        this.updateDefaultAuthorizationHeader(`Bearer ${token}`);
    }
    hasAuthTokenInHeader({ headers: { common } }) {
        return common && common.Authorization !== undefined;
    }
    updateConfigHeader(_a, headerKey, headerValue) {
        var { headers } = _a, restConfig = __rest(_a, ["headers"]);
        return Object.assign({ headers: Object.assign(Object.assign({}, headers), { [headerKey]: headerValue }) }, restConfig);
    }
    updateConfigWithNewToken(currentUser, config) {
        return __awaiter(this, void 0, void 0, function* () {
            const newToken = yield currentUser.getIdToken();
            const updatedConfig = this.updateConfigHeader(config, "Authorization", `Bearer ${newToken}`);
            // ensure instance default header is also updated for subsequent requests
            this.updateAuthorizationHeaderToken(newToken);
            return updatedConfig;
        });
    }
    checkAuthToken(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (config.noAuth) {
                return config;
            }
            const currentUser = this.firebase.auth().currentUser;
            if (currentUser) {
                if (this.hasAuthTokenInHeader(config)) {
                    // token already present in header
                    return config;
                }
                else {
                    try {
                        // no token found, fetch new token
                        // send the updated header
                        return yield this.updateConfigWithNewToken(currentUser, config);
                    }
                    catch (error) {
                        console.error("Error while fetching a new id token");
                        return this.rejectedErrorPromise(error);
                    }
                }
            }
            else {
                console.error("No valid user for this request");
                return this.rejectedErrorPromise(config);
            }
        });
    }
    checkAuthTokenExpiry(error) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Ignore any error which is not due to token expiry
            if ((error && !error.response) ||
                (error.response && error.response.status !== 401)) {
                return this.rejectedErrorPromise(error);
            }
            // check if the error is due to a token revoked
            if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === "token revoked") {
                // need to handle the rerouting to login page
                window.location.href = this.loginURL;
                return this.rejectedErrorPromise(error);
            }
            const currentUser = this.firebase.auth().currentUser;
            if (currentUser) {
                try {
                    // Detected token expiry, fetching new id token
                    const updatedConfig = yield this.updateConfigWithNewToken(currentUser, error.config);
                    // sending back the request
                    return this.replayRequest(updatedConfig);
                }
                catch (error) {
                    console.error("Error while fetching a new id token");
                    return this.rejectedErrorPromise(error);
                }
            }
            else {
                console.error("No valid user to fetch new id token");
                return this.rejectedErrorPromise(error);
            }
        });
    }
}
export default HttpClient;
